﻿import React, { useState } from 'react'
import { WikiArticleVersion } from '../types/wiki'
import { Button } from './ui'
import { XMarkIcon, EyeIcon, CodeBracketIcon } from '@heroicons/react/24/outline'

interface VersionDiffProps {
  versions: WikiArticleVersion[]
  onClose: () => void
}

const VersionDiff: React.FC<VersionDiffProps> = ({ versions, onClose }) => {
  const [selectedVersions, setSelectedVersions] = useState<{
    from: WikiArticleVersion | null
    to: WikiArticleVersion | null
  }>({ from: null, to: null })
  const [viewMode, setViewMode] = useState<'rendered' | 'html'>('rendered')

  // Função para renderizar HTML ou mostrar código
  const renderContent = (content: string) => {
    if (viewMode === 'html') {
      return (
        <pre className="whitespace-pre-wrap text-sm font-mono bg-gray-100 dark:bg-gray-800 p-4 rounded overflow-x-auto">
          {content}
        </pre>
      )
    }
    
    return (
      <div 
        className="prose dark:prose-invert max-w-none"
        dangerouslySetInnerHTML={{ __html: content }}
      />
    )
  }

  // Função para gerar diff visual simples
  const generateDiff = (fromContent: string, toContent: string) => {
    const fromLines = fromContent.split('\n')
    const toLines = toContent.split('\n')
    
    const diff = []
    const maxLines = Math.max(fromLines.length, toLines.length)
    
    for (let i = 0; i < maxLines; i++) {
      const fromLine = fromLines[i] || ''
      const toLine = toLines[i] || ''
      
      if (fromLine === toLine) {
        diff.push({
          type: 'unchanged',
          from: fromLine,
          to: toLine,
          lineNumber: i + 1
        })
      } else if (fromLine && !toLine) {
        diff.push({
          type: 'removed',
          from: fromLine,
          to: '',
          lineNumber: i + 1
        })
      } else if (!fromLine && toLine) {
        diff.push({
          type: 'added',
          from: '',
          to: toLine,
          lineNumber: i + 1
        })
      } else {
        diff.push({
          type: 'modified',
          from: fromLine,
          to: toLine,
          lineNumber: i + 1
        })
      }
    }
    
    return diff
  }

  const handleVersionSelect = (version: WikiArticleVersion, side: 'from' | 'to') => {
    setSelectedVersions(prev => ({
      ...prev,
      [side]: version
    }))
  }

  const canCompare = selectedVersions.from && selectedVersions.to && 
    selectedVersions.from.version_number !== selectedVersions.to.version_number

  const diff = canCompare && selectedVersions.from && selectedVersions.to ? generateDiff(
    selectedVersions.from.body, 
    selectedVersions.to.body
  ) : []

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-7xl h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Comparar Versões
          </h2>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setViewMode('rendered')}
                variant={viewMode === 'rendered' ? 'solid' : 'outline'}
                size="sm"
                className="flex items-center gap-1"
              >
                <EyeIcon className="h-4 w-4" />
                Renderizado
              </Button>
              <Button
                onClick={() => setViewMode('html')}
                variant={viewMode === 'html' ? 'solid' : 'outline'}
                size="sm"
                className="flex items-center gap-1"
              >
                <CodeBracketIcon className="h-4 w-4" />
                HTML
              </Button>
            </div>
            <Button onClick={onClose} variant="outline" size="sm">
              <XMarkIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {!canCompare ? (
            /* Seleção de Versões */
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Versão FROM */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Versão Anterior
                  </h3>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {versions.map((version) => (
                      <button
                        key={version.version_number}
                        onClick={() => handleVersionSelect(version, 'from')}
                        className={`w-full p-3 text-left rounded-lg border transition-colors ${
                          selectedVersions.from?.version_number === version.version_number
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium">v{version.version_number}</span>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {new Date(version.created_at).toLocaleDateString('pt-BR')}
                          </span>
                        </div>
                        {version.change_summary && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {version.change_summary}
                          </p>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Versão TO */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Versão Posterior
                  </h3>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {versions.map((version) => (
                      <button
                        key={version.version_number}
                        onClick={() => handleVersionSelect(version, 'to')}
                        className={`w-full p-3 text-left rounded-lg border transition-colors ${
                          selectedVersions.to?.version_number === version.version_number
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium">v{version.version_number}</span>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {new Date(version.created_at).toLocaleDateString('pt-BR')}
                          </span>
                        </div>
                        {version.change_summary && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {version.change_summary}
                          </p>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {selectedVersions.from && selectedVersions.to && (
                <div className="mt-6 text-center">
                  <Button
                    onClick={() => {}}
                    className="px-6"
                    disabled={!canCompare}
                  >
                    Comparar Versões
                  </Button>
                </div>
              )}
            </div>
          ) : (
            /* Comparação Lado-a-Lado */
            <div className="flex h-full min-h-0">
              {/* Versão FROM */}
              <div className="flex-1 flex flex-col min-w-0 min-h-0 border-r border-gray-200 dark:border-gray-700">
                <div className="p-4 bg-red-50 dark:bg-red-900/10 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="font-medium text-red-800 dark:text-red-200">
                    v{selectedVersions.from?.version_number} - {selectedVersions.from?.change_summary}
                  </h3>
                  <p className="text-sm text-red-600 dark:text-red-400">
                    {selectedVersions.from && new Date(selectedVersions.from.created_at).toLocaleString('pt-BR')}
                  </p>
                </div>
                <div className="p-4 overflow-y-auto flex-1 min-h-0">
                  {selectedVersions.from && renderContent(selectedVersions.from.body)}
                </div>
              </div>

              {/* Versão TO */}
              <div className="flex-1 flex flex-col min-w-0 min-h-0">
                <div className="p-4 bg-green-50 dark:bg-green-900/10 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="font-medium text-green-800 dark:text-green-200">
                    v{selectedVersions.to?.version_number} - {selectedVersions.to?.change_summary}
                  </h3>
                  <p className="text-sm text-green-600 dark:text-green-400">
                    {selectedVersions.to && new Date(selectedVersions.to.created_at).toLocaleString('pt-BR')}
                  </p>
                </div>
                <div className="p-4 overflow-y-auto flex-1 min-h-0">
                  {selectedVersions.to && renderContent(selectedVersions.to.body)}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default VersionDiff
